import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Modal from '@/components/atoms/Modal';
import { SvyButton } from '@/components/savory/SvyButton';
import { SvySwitch } from '@/components/savory/SvySwitch';
import { getUserToken } from '@/redux/user';
import { useGetBrandQuery, useUpdateBrandMutation } from '@/services/innovorder/brand/brand.endpoints';

import { Description, SwitchWrapper, ToggleHeader, ToggleLabel, ToggleRow } from './GrantsSettingsModal.style';

interface GrantsSettingsModalProps {
    isOpen: boolean;
    onHide: () => void;
    brandId: number;
}

export const GrantsSettingsModal: React.FunctionComponent<GrantsSettingsModalProps> = ({ isOpen, onHide, brandId }) => {
    const token = useSelector(getUserToken);
    const { data: brand } = useGetBrandQuery({ brandId, token }, { refetchOnMountOrArgChange: true });
    const [updateBrand, { isLoading: isSaving, isSuccess, reset }] = useUpdateBrandMutation();

    const [shouldBlock, setShouldBlock] = useState(false);

    useEffect(() => {
        if (isOpen && brand) {
            setShouldBlock(brand.shouldBlockTRDPaymentMethods);
        }
        if (!isOpen) {
            reset();
        }
    }, [isOpen, brand, reset]);

    useEffect(() => {
        if (isSuccess) {
            onHide();
            reset();
        }
    }, [isSuccess, onHide, reset]);

    const handleSave = () => {
        if (!brand?.name) return;
        updateBrand({
            brandId,
            token,
            name: brand.name,
            shouldBlockTRDPaymentMethods: shouldBlock,
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onHide={onHide}
            width={520}
            title="grant.settings.modal.title"
            closeMessage="grant.settings.modal.cancel"
            buttons={
                <SvyButton variant="default" color="primary" onClick={handleSave} disabled={isSaving || !brand}>
                    <FormattedMessage id="grant.settings.modal.save" />
                </SvyButton>
            }
        >
            <Description>
                <FormattedMessage id="grant.settings.modal.subtitle" />
            </Description>
            <ToggleRow>
                <ToggleHeader>
                    <SwitchWrapper>
                        <SvySwitch value={shouldBlock} onChange={() => setShouldBlock((prev) => !prev)} />
                    </SwitchWrapper>
                    <ToggleLabel>
                        <FormattedMessage id="grant.settings.modal.toggle.label" />
                    </ToggleLabel>
                </ToggleHeader>
                <Description>
                    <FormattedMessage id="grant.settings.modal.toggle.description" />
                </Description>
            </ToggleRow>
        </Modal>
    );
};
